/**
 * The **pilot (on-foot) action vocabulary** — the RAW action catalog an unmounted pilot draws
 * from (Core Book pp. 73–76), as a single data-driven table. The pilot twin of
 * {@link "./mechActions".MECH_ACTIONS} / {@link "./npcActions".NPC_ACTIONS}: deliberately its
 * OWN, genuinely **narrower** vocabulary — RAW p.74's "unmounted characters make all attacks and
 * saves by adding GRIT" replaces the mech's HASE-driven verbs, and pilots carry no systems at all
 * (no Barrage/Ram/Grapple/Improvised Attack/Stabilize/Overcharge/Quick Tech/Full Tech/the reactor
 * cluster — `techAttack` is frozen but unconsumed). A pilot's own weapon attack is **Fight**
 * (RAW p.75), not Skirmish — a distinct catalog id (`"fight"`) so a pilot and its mech carry
 * independent once-per-turn ledgers (`economy.ts`'s `actionIdForAttackLike`).
 *
 * Like the mech/NPC tables, this is the **seam** between the UI and the resolving engine: every
 * entry carries a {@link PilotActionStatus}. Only the `"live"` ones resolve through an existing
 * engine path today — **Fight** (the pilot's own weapon attack, riding the shared
 * attack-resolution flow, generic over `Combatant.kind`), **Boost** (generic over any combatant's
 * `base.speed` — no kind gate to extend), **Hide** / **Search** (generic stealth/contest reducers
 * — Search reads GRIT off a pilot's HASE-axes-as-grit freeze), **Disengage** (generic — lands
 * `Disengaged` off the same status-only mechanism a mech uses), **Mount** (`request_mount` —
 * climb back into your own adjacent Unpiloted mech; clears Unpiloted, removes the pilot
 * combatant, stamps the mech into `activated`), **Jockey** (`request_jockey`'s contested-GRIT
 * attach against an adjacent enemy mech; the repeatable Distract/Shred/Damage follow-up is
 * `request_jockey_menu`, and the carrier's own throw-off is `request_shake_off`, neither of which
 * gets its own catalog row — mirrors Grapple's break/release, which also stay off
 * `MECH_ACTIONS`), **Ride** (`request_ride`'s consent-based allied MOUNT: Jockey's co-location
 * plumbing with the side gate INVERTED (same-side required, not mismatch) and no contest — a
 * willing ally always attaches; a DISTINCT catalog id from both `mount` (climb into your OWN
 * unpiloted mech) and `jockey` (the hostile attach)), and **Reload** (`request_reload` clears one
 * Loading pilot weapon's `loaded:<instanceKey>` resource via `@lancer-vtt/rules`' `reload.ts`).
 * The rest render as honest **"resolves manually — engine pending"** reference rows: **Activate**
 * (RAW's generic "activate a nearby/mounted system" action — a wider surface than the
 * granted-affordance list this registry already enumerates separately), **Skill Check** (an
 * arbitrary GM-adjudicated skill roll), and **Overwatch** (mirrors `MECH_ACTIONS`'s own Overwatch
 * row: the manual catalog button is reference-only — the automatic movement-triggered reaction is
 * a separate system, generic over attacker kind already).
 *
 * **Pure data** — no I/O, no React. `enumerateActions` (`actionRegistry.ts`) reads it for a
 * `kind === "pilot"` combatant, mirroring the NPC branch exactly.
 */

import type { MechActionRoute, MechActionValueKind } from "./mechActions";

/** A pilot action's action-economy cost class (RAW, Core p. 73-76). */
export type PilotActionCost = "Quick" | "Full" | "Reaction";

/**
 * Whether a pilot action is wired to a real resolution path today.
 * - `"live"` — resolves through an existing engine path, generic over `Combatant.kind`.
 * - `"engine-pending"` — RAW-faithful reference only; no resolving engine path yet.
 */
export type PilotActionStatus = "live" | "engine-pending";

/**
 * A short, RAW-grounded classifier for a pilot action — the per-row "category chip" (mirrors
 * `MechActionCategory`/`NpcActionCategory`; widened with `"SETUP"` for Prepare, the same reason
 * `NpcActionCategory` carries it).
 */
export type PilotActionCategory = "ATTACK" | "MELEE" | "MOVE" | "STEALTH" | "SETUP" | "SUPPORT";

/** One RAW pilot (on-foot) action: a stable id, its RAW name + cost class + category + blurb + wiring status. */
export type PilotAction = {
  /** Stable key (also the React list key); RAW name lowercased/underscored. */
  id: string;
  /** The RAW action name as printed (e.g. "Fight"). */
  name: string;
  /** The action-economy cost class — drives the grid column. */
  cost: PilotActionCost;
  /** The action's kind (ATTACK / MOVE / …) — the per-row chip beside the name. */
  category: PilotActionCategory;
  /** A short RAW blurb of the action's effect. */
  blurb: string;
  /** Whether it resolves through a live engine path today, or is reference-only. */
  status: PilotActionStatus;
  /** How this action resolves — the SAME {@link MechActionRoute} the unified registry keys on. */
  route: MechActionRoute;
  /** Optional base-derived **value badge** kind (Boost's Speed) — same badge vocabulary as the mech table. */
  value?: MechActionValueKind;
};

/**
 * The RAW pilot (on-foot) action catalog (Core Book pp. 73–76). Ordered Fight-first (the live
 * attack, mirroring Skirmish's position in the mech/NPC tables), then the remaining quick
 * actions, the full actions, and the reaction.
 */
export const PILOT_ACTIONS: readonly PilotAction[] = [
  {
    id: "fight",
    name: "Fight",
    cost: "Full",
    category: "ATTACK",
    blurb: "Attack with an equipped pilot weapon (RAW p.75) — the on-foot counterpart of Skirmish, a FULL action rather than Quick.",
    status: "live",
    route: "attack",
  },
  {
    id: "boost",
    name: "Boost",
    cost: "Quick",
    category: "MOVE",
    blurb: "Move a number of spaces up to your Speed (in addition to your standard move).",
    status: "live",
    route: "reducer",
    value: "speed",
  },
  {
    id: "hide",
    name: "Hide",
    cost: "Quick",
    category: "STEALTH",
    blurb: "If you have cover or are obscured, become Hidden until you attack, are seen, or lose the condition.",
    status: "live",
    route: "reducer",
  },
  {
    id: "search",
    name: "Search",
    cost: "Quick",
    category: "STEALTH",
    blurb: "Contested GRIT (standing in for Systems/Agility on foot) vs. a Hidden character within Sensors; a win OR a tie reveals them.",
    status: "live",
    route: "reducer",
  },
  {
    id: "activate",
    name: "Activate",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Activate a mounted system, piece of scenery, or gear with the Activation tag within reach.",
    status: "engine-pending",
    route: "activate",
  },
  {
    id: "skill_check",
    name: "Skill Check",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Attempt a task with a meaningful chance of failure: roll a skill check against a GM-set difficulty.",
    status: "engine-pending",
    route: "reducer",
  },
  {
    id: "reload",
    name: "Reload",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Reload one equipped Loading pilot weapon.",
    status: "live",
    route: "reducer",
  },
  {
    id: "disengage",
    name: "Disengage",
    cost: "Full",
    category: "MOVE",
    blurb: "For the rest of the turn your movement doesn't provoke reactions and ignores Engagement.",
    status: "live",
    route: "reducer",
  },
  {
    id: "mount",
    name: "Mount",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Climb into your own adjacent, Unpiloted mech (RAW p.70) — clears Unpiloted, you're removed.",
    status: "live",
    route: "reducer",
  },
  {
    id: "jockey",
    name: "Jockey",
    cost: "Full",
    category: "MELEE",
    blurb: "Contested GRIT vs. an adjacent hostile mech's HULL; on success, climb aboard and ride along, menacing it each turn.",
    status: "live",
    route: "reducer",
  },
  {
    id: "ride",
    name: "Ride",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Climb onto a willing adjacent allied mech and ride along, sharing its space (RAW p.70) — no contest, no menace.",
    status: "live",
    route: "reducer",
  },
  {
    id: "ride_dismount",
    name: "Dismount (ally)",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Climb off a willing ally's mech into an adjacent free cell (RAW p.70) — clears the ride, spends no movement.",
    status: "live",
    route: "reducer",
  },
  {
    id: "prepare",
    name: "Prepare",
    cost: "Quick",
    category: "SETUP",
    blurb: "Prepare a quick action to take as a reaction (player Prepare rules).",
    status: "engine-pending",
    route: "reducer",
  },
  {
    id: "overwatch",
    name: "Overwatch",
    cost: "Reaction",
    category: "ATTACK",
    blurb: "When a character starts moving within threat range, Fight against them as a reaction.",
    status: "engine-pending",
    route: "reaction",
  },
];
